import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../components/ui/utils';

export interface SectionTab {
  to: string;
  label: string;
  /** Счётчик справа от подписи — например число публикаций во вкладке. */
  count?: number | string | null;
  end?: boolean;
}

interface SectionTabsProps {
  tabs: SectionTab[];
  className?: string;
  /**
   * Параметры адреса, которые вкладки несут с собой: например период
   * «Статистики» — переключение вкладки не должно его сбрасывать.
   */
  keepParams?: readonly string[];
}

/** `to` вкладки + перенесённые параметры текущего адреса. */
export function withKeptParams(to: string, search: string, keep: readonly string[] | undefined): string {
  if (!keep?.length) return to;
  const current = new URLSearchParams(search);
  const [path, own = ''] = to.split('?');
  const next = new URLSearchParams(own);
  for (const key of keep) {
    const value = current.get(key);
    if (value !== null && !next.has(key)) next.set(key, value);
  }
  const query = next.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Вкладки раздела: подпись + подчёркивание 2px у активной.
 * Отличаются от `Tabs` из shadcn (капсула на серой подложке) намеренно —
 * капсулы остаются для переключателей внутри карточки, подчёркивание
 * обозначает навигацию верхнего уровня.
 */
export function SectionTabs({ tabs, className, keepParams }: SectionTabsProps): JSX.Element {
  const { search } = useLocation();
  return (
    <div className={cn('-mx-1 overflow-x-auto', className)}>
      <nav className="flex min-w-max items-center gap-1 border-b px-1">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={withKeptParams(tab.to, search, keepParams)}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                'group relative inline-flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors',
                'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors',
                isActive
                  ? 'text-foreground after:bg-primary'
                  : 'text-muted-foreground after:bg-transparent hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span>{tab.label}</span>
                {tab.count != null && tab.count !== '' && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[11px] leading-none tabular-nums transition-colors',
                      isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {typeof tab.count === 'number' ? tab.count.toLocaleString('ru-RU') : tab.count}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
